// «Archivados» in Explorar canales (shared/API.md §41.4): the team's archived public channels
// (`GET /teams/{id}/channels/deleted`, only when the server's ExperimentalViewArchivedChannels is
// on — otherwise the tab does not exist) plus the reader's own archived private channels from the
// store. Opening one is the ordinary read-only conversation (§26); «Restaurar canal» lives in its
// info panel when permitted.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import type { ArchivedChannelsResult } from '../../../data/features/nav';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { EmptyState, InlineStatus } from '../../kit/Kit';

/** The archived listing for a team, asked once per dialog open (null while asking). */
export function useArchivedChannels(teamId: string, tick = 0): ArchivedChannelsResult | null {
  const [result, setResult] = useState<ArchivedChannelsResult | null>(null);
  useEffect(() => {
    let live = true;
    setResult(null);
    void data.call('archivedChannels', teamId).then(
      (r) => live && setResult(r),
      () => live && setResult({ error: 'network' }),
    );
    return () => {
      live = false;
    };
  }, [teamId, tick]);
  return result;
}

function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

export function ArchivedChannelsList({ result, query, onOpen, onRetry }: { result: ArchivedChannelsResult | null; query: string; onOpen(channelId: string): void; onRetry(): void }) {
  const term = fold(query.trim());
  const channels = result && 'available' in result && result.available ? result.channels : null;
  const visible = useMemo(() => (channels ?? []).filter((c) => !term || fold(c.displayName).includes(term) || c.name.includes(term)), [channels, term]);
  if (result === null) return <InlineStatus kind="loading" text={t('web.people.searching')} />;
  if ('error' in result) return <InlineStatus kind="error" text={t('browse.failed')} onRetry={onRetry} />;
  if (!result.available) return <EmptyState title={t('web.archived.unavailable')} />;
  if (!visible.length) return <EmptyState title={term ? tf('web.people.browse.noMatches', query.trim()) : t('web.archived.empty')} />;
  return (
    <div className="people-list" role="listbox" aria-label={t('web.archived.tab')}>
      {visible.map((channel) => (
        <button type="button" key={channel.id} role="option" aria-selected={false} className="list-row clickable people-row" data-channel-id={channel.id} onClick={() => onOpen(channel.id)}>
          <span className="list-row-leading">
            <span className="people-glyph">
              <Icon name="archive" size={16} />
            </span>
          </span>
          <span className="list-row-text">
            <span className="list-row-title">
              {channel.displayName}
              {channel.type === 'P' ? <Icon name="lock" size={12} style={{ marginLeft: 6, verticalAlign: -1 }} /> : null}
            </span>
            <span className="list-row-subtitle">{channel.purpose || t('web.archived.label')}</span>
          </span>
          <span className="list-row-trailing">
            <span className="people-trailing-action quiet">{t('people.open')}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
